export type StoredNodeMetadata = Record<string, unknown>;

export function extractMetadataCount(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    if (!value || typeof value !== 'object') {
        return null;
    }

    const record = value as Record<string, unknown>;
    const candidates = [
        record.count,
        record.row_count,
        record.output_row_count,
        record.result_count,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === 'number' && Number.isFinite(candidate)) {
            return candidate;
        }
    }

    return null;
}

export function getFallbackRootCount(response: unknown): number | null {
    if (!response || typeof response !== 'object') {
        return null;
    }

    const record = response as Record<string, unknown>;

    if (typeof record.row_count === 'number' && Number.isFinite(record.row_count)) {
        return record.row_count;
    }

    if (Array.isArray(record.data)) {
        return record.data.length;
    }

    return null;
}

export function normalizeNodeMetadata(
    rawMetadata: unknown,
    response: unknown,
    rootNodeId?: string | null,
): StoredNodeMetadata | undefined {
    const normalized: StoredNodeMetadata = {};

    if (Array.isArray(rawMetadata)) {
        for (const entry of rawMetadata) {
            if (!entry || typeof entry !== 'object') {
                continue;
            }

            const record = entry as Record<string, unknown>;
            const nodeId =
                typeof record.node_id === 'string' ? record.node_id
                    : typeof record.id === 'string' ? record.id
                        : null;
            const count = extractMetadataCount(record);

            if (nodeId) {
                normalized[nodeId] = count !== null ? { ...record, count } : record;
            }
        }
    } else if (rawMetadata && typeof rawMetadata === 'object') {
        for (const [nodeId, metadata] of Object.entries(rawMetadata as Record<string, unknown>)) {
            const count = extractMetadataCount(metadata);
            if (count !== null && metadata && typeof metadata === 'object') {
                normalized[nodeId] = { ...(metadata as Record<string, unknown>), count };
            } else if (count !== null) {
                normalized[nodeId] = { count };
            } else {
                normalized[nodeId] = metadata;
            }
        }
    }

    if (rootNodeId && normalized[rootNodeId] === undefined) {
        const fallbackCount = getFallbackRootCount(response);
        if (fallbackCount !== null) {
            normalized[rootNodeId] = { count: fallbackCount };
        }
    } else if (rootNodeId) {
        const existing = normalized[rootNodeId];
        const existingCount = extractMetadataCount(existing);
        const fallbackCount = getFallbackRootCount(response);
        if (existing && existingCount === null && fallbackCount !== null && typeof existing === 'object') {
            normalized[rootNodeId] = { ...(existing as Record<string, unknown>), count: fallbackCount };
        }
    }

    return Object.keys(normalized).length > 0 ? normalized : undefined;
}
